import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WhatsAppRegistration = ({ language = 'fr', onWhatsAppRegister, isLoading }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState('phone'); // phone, verification, success

  const validatePhoneNumber = (phone) => {
    return /^6[0-9]{8}$/?.test(phone);
  };

  const handlePhoneSubmit = () => {
    if (!phoneNumber?.trim()) {
      setError(language === 'fr' ? 'Le numéro de téléphone est requis' : 'Phone number is required');
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      setError(language === 'fr' ? 'Format invalide. Utilisez: 6XXXXXXXX' : 'Invalid format. Use: 6XXXXXXXX');
      return;
    }

    setError('');
    setStep('verification');
    
    if (onWhatsAppRegister) {
      onWhatsAppRegister({ phoneNumber, method: 'whatsapp' });
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      language === 'fr' ?'Bonjour, je souhaite créer un compte AutoCasse via WhatsApp. Mon numéro: '+ phoneNumber :'Hello, I want to create an AutoCasse account via WhatsApp. My number: ' + phoneNumber
    );
    const whatsappUrl = `https://wa.me/237${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="MessageCircle" size={32} className="text-success" />
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {language === 'fr' ? 'Inscription via WhatsApp' : 'WhatsApp Registration'}
        </h2>
        <p className="text-muted-foreground">
          {language === 'fr' ?'Créez votre compte rapidement avec WhatsApp' :'Create your account quickly with WhatsApp'
          }
        </p>
      </div>
      {/* Benefits */}
      <div className="bg-success/5 border border-success/20 rounded-lg p-4">
        <h3 className="font-medium text-success mb-3">
          {language === 'fr' ? 'Avantages WhatsApp:' : 'WhatsApp Benefits:'}
        </h3>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Icon name="Zap" size={16} className="text-success" />
            <span className="text-sm text-foreground">
              {language === 'fr' ? 'Inscription rapide en 2 minutes' : 'Quick 2-minute registration'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MessageCircle" size={16} className="text-success" />
            <span className="text-sm text-foreground">
              {language === 'fr' ? 'Communication directe avec les vendeurs' : 'Direct communication with sellers'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={16} className="text-success" />
            <span className="text-sm text-foreground">
              {language === 'fr' ? 'Notifications instantanées' : 'Instant notifications'}
            </span>
          </div>
        </div>
      </div>
      {step === 'phone' && (
        <div className="space-y-4">
          <Input
            label={language === 'fr' ? 'Numéro WhatsApp' : 'WhatsApp Number'}
            type="tel"
            placeholder="6XXXXXXXX"
            value={phoneNumber}
            onChange={(e) => {
              setPhoneNumber(e?.target?.value);
              if (error) setError('');
            }}
            error={error}
            required
            description={language === 'fr' ? 'Votre numéro WhatsApp camerounais' : 'Your Cameroon WhatsApp number'}
          />

          <Button
            variant="success"
            fullWidth
            onClick={handlePhoneSubmit}
            loading={isLoading}
            iconName="MessageCircle"
            iconPosition="left"
            iconSize={16}
          >
            {language === 'fr' ? 'Continuer avec WhatsApp' : 'Continue with WhatsApp'}
          </Button>
        </div>
      )}
      {step === 'verification' && (
        <div className="space-y-6">
          {/* Step Indicator */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-success text-success-foreground rounded-full flex items-center justify-center">
                <span className="text-sm font-bold">1</span>
              </div>
              <div>
                <p className="font-medium text-foreground">
                  {language === 'fr' ? 'Numéro confirmé' : 'Number Confirmed'}
                </p>
                <p className="text-sm text-muted-foreground">+237 {phoneNumber}</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mt-1">
                <span className="text-sm font-bold">2</span>
              </div>
              <div>
                <p className="font-medium text-foreground mb-2">
                  {language === 'fr' ? 'Ouvrir WhatsApp' : 'Open WhatsApp'}
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  {language === 'fr' ?'Cliquez sur le bouton ci-dessous pour ouvrir WhatsApp et envoyer le message de vérification.' :'Click the button below to open WhatsApp and send the verification message.'
                  }
                </p>
                <Button
                  variant="success"
                  onClick={openWhatsApp}
                  iconName="ExternalLink"
                  iconPosition="right"
                  iconSize={16}
                >
                  {language === 'fr' ? 'Ouvrir WhatsApp' : 'Open WhatsApp'}
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-muted text-muted-foreground rounded-full flex items-center justify-center mt-1">
                <span className="text-sm font-bold">3</span>
              </div>
              <div>
                <p className="font-medium text-foreground mb-2">
                  {language === 'fr' ? 'Attendre la confirmation' : 'Wait for Confirmation'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ?'Notre équipe vous répondra dans WhatsApp pour finaliser votre inscription.' :'Our team will respond in WhatsApp to finalize your registration.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-accent mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-accent mb-1">
                  {language === 'fr' ? 'Temps de réponse' : 'Response Time'}
                </p>
                <p className="text-muted-foreground">
                  {language === 'fr' ?'Nous répondons généralement en moins de 30 minutes pendant les heures ouvrables (8h-18h).' :'We typically respond within 30 minutes during business hours (8AM-6PM).'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Alternative Option */}
          <div className="text-center pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground mb-3">
              {language === 'fr' ? 'Ou utilisez l\'inscription classique' : 'Or use standard registration'}
            </p>
            <Button
              variant="outline"
              onClick={() => setStep('phone')}
              iconName="ArrowLeft"
              iconPosition="left"
              iconSize={16}
            >
              {language === 'fr' ? 'Retour' : 'Go Back'}
            </Button>
          </div>
        </div>
      )}
      {/* Security Notice */}
      <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-warning mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-warning mb-1">
              {language === 'fr' ? 'Sécurité' : 'Security'}
            </p>
            <p className="text-muted-foreground">
              {language === 'fr' ?'AutoCasse ne vous demandera jamais vos mots de passe ou informations bancaires via WhatsApp.' :'AutoCasse will never ask for your passwords or banking information via WhatsApp.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppRegistration;